/**
 * Utility functions.
 */
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Merge Tailwind classes with clsx.
 * Handles conflicts properly (e.g., "p-4 p-2" -> "p-2")
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format a number as currency.
 */
export function formatCurrency(
  value: number | null | undefined,
  options: {
    decimals?: number;
    compact?: boolean;
    nullDisplay?: string;
    signed?: boolean;
  } = {}
): string {
  const { decimals = 2, compact = false, nullDisplay = '-', signed = false } = options;

  if (value == null || !Number.isFinite(value)) {
    return nullDisplay;
  }

  if (compact && Math.abs(value) >= 1000) {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      notation: 'compact',
      maximumFractionDigits: 1,
    });
    const formatted = formatter.format(value);
    return signed && value > 0 ? `+${formatted}` : formatted;
  }

  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
  return signed && value > 0 ? `+${formatted}` : formatted;
}

export function formatCurrencyCompact(
  value: number | null | undefined,
  options: { decimals?: number; nullDisplay?: string; signed?: boolean } = {}
): string {
  return formatCurrency(value, { ...options, compact: true });
}

export function formatCompactNumber(
  value: number | null | undefined,
  options: { decimals?: number; nullDisplay?: string; signed?: boolean } = {}
): string {
  const { decimals = 1, nullDisplay = '-', signed = false } = options;
  if (value == null || !Number.isFinite(value)) {
    return nullDisplay;
  }

  const formatted = new Intl.NumberFormat('en-US', {
    notation: 'compact',
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  }).format(value);
  return signed && value > 0 ? `+${formatted}` : formatted;
}

/**
 * Format a number as percentage.
 */
export function formatPercent(value: number, decimals = 1): string {
  const prefix = value > 0 ? '+' : '';
  return `${prefix}${value.toFixed(decimals)}%`;
}

export function formatPercentFromDecimal(
  value: number | null | undefined,
  options: { decimals?: number; nullDisplay?: string; signed?: boolean } = {}
): string {
  const { decimals = 1, nullDisplay = '-', signed = false } = options;
  if (value == null || !Number.isFinite(value)) {
    return nullDisplay;
  }

  const pct = value * 100;
  const prefix = signed && pct > 0 ? '+' : '';
  return `${prefix}${pct.toFixed(decimals)}%`;
}

/**
 * Format a number with commas.
 */
export function formatNumber(value: number, decimals = 2): string {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

export function formatDuration(
  value: number | null | undefined,
  options: {
    unit?: 'ms' | 'seconds' | 'minutes' | 'hours';
    nullText?: string;
    hours?: boolean;
  } = {}
): string {
  const { unit = 'seconds', nullText = '-', hours = false } = options;
  if (value == null || !Number.isFinite(value)) {
    return nullText;
  }

  let seconds = value;
  if (unit === 'ms') seconds = value / 1000;
  if (unit === 'minutes') seconds = value * 60;
  if (unit === 'hours') seconds = value * 3600;

  if (hours || seconds >= 3600) {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }

  if (seconds >= 60) {
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    return s > 0 ? `${m}m ${s}s` : `${m}m`;
  }

  return `${seconds.toFixed(seconds < 10 && seconds !== Math.floor(seconds) ? 1 : 0)}s`;
}

/**
 * Format a date string.
 */
export function formatDate(
  date: string | Date,
  options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }
): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', options);
}

/**
 * Format a date for display in tables (compact).
 */
export function formatDateCompact(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' });
}

/**
 * Format an age given in HOURS as a relative string ("just now" / "3h ago" / "2d ago").
 * Canonical home for the research-workflow age pills (was duplicated per component).
 */
export function formatAgeHours(hours: number): string {
  if (hours < 1) {
    return 'just now';
  } else if (hours < 24) {
    return `${Math.round(hours)}h ago`;
  } else {
    return `${Math.round(hours / 24)}d ago`;
  }
}

/**
 * Get color class based on value (positive/negative).
 */
export function getValueColor(value: number): string {
  if (value > 0) return 'text-positive';
  if (value < 0) return 'text-negative';
  return 'text-muted-foreground';
}

/**
 * Sleep for a given number of milliseconds.
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Safely parse a string to float, returning null for invalid/empty values.
 * Prevents NaN from propagating through the system.
 *
 * @param value - String to parse (may be undefined, null, or empty)
 * @returns Parsed number or null if invalid
 */
export function safeParseFloat(value: string | undefined | null): number | null {
  if (!value) return null;
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * Safely parse a string to integer, returning null for invalid/empty values.
 * Prevents NaN from propagating through the system.
 *
 * @param value - String to parse (may be undefined, null, or empty)
 * @param radix - Base for parsing (default: 10)
 * @returns Parsed integer or null if invalid
 */
export function safeParseInt(value: string | undefined | null, radix: number = 10): number | null {
  if (!value) return null;
  const parsed = parseInt(value, radix);
  return Number.isNaN(parsed) ? null : parsed;
}
